import { createEffectScope } from 'fine-jsx'
import * as Vue from 'vue'

import type * as pub from '#core'

import { DocumentView, type ViewType } from '../document-view.ts'

import { _vuePlainReadonly, _vueWritable } from './utils.ts'
import {
  type VueAudioClip,
  type VueAudioTrack,
  type VueImageClip,
  VueNodeView,
  VueParentNode,
  type VueTextClip,
  type VueTimeline,
  type VueVideoClip,
  type VueVideoTrack,
} from './vue-nodes.ts'

export interface VueTypeMap {
  timeline: VueTimeline
  'track:video': VueVideoTrack
  'track:audio': VueAudioTrack
  'clip:video': VueVideoClip
  'clip:audio': VueAudioClip
  'clip:image': VueImageClip
  'clip:text': VueTextClip
}

export class VueDocument extends DocumentView<VueTypeMap> implements pub.Document {
  readonly vueScope = Vue.effectScope()
  readonly fineJsxScope = createEffectScope()

  declare resolution: pub.Document['resolution']
  declare frameRate: pub.Document['frameRate']
  declare readonly currentTime: pub.Document['currentTime']
  declare readonly _currentFrameTime: pub.Document['_currentFrameTime']
  declare readonly duration: pub.Document['duration']
  declare readonly timeline: pub.Document['timeline']
  declare readonly assets: pub.Document['assets']
  declare readonly nodes: pub.Document['nodes']
  declare readonly links: pub.Document['links']
  declare readonly isEmpty: pub.Document['isEmpty']
  /** @internal */
  declare readonly activeClipIsStalled: pub.Document['activeClipIsStalled']

  constructor(options: { doc: pub.Document }) {
    const { doc } = options
    super(doc)

    Vue.markRaw(this)
    Vue.markRaw(doc)
    ;(['resolution', 'frameRate'] satisfies (keyof pub.Document)[]).forEach((key) =>
      _vueWritable(this, doc, key),
    )
    ;(
      [
        'currentTime',
        '_currentFrameTime',
        'duration',
        'timeline',
        'assets',
        'nodes',
        'links',
        'isEmpty',
        'activeClipIsStalled',
      ] satisfies (keyof pub.Document)[]
    ).forEach((key) => _vuePlainReadonly(this, doc, key))

    this._init()
  }

  createNode = this.doc.createNode.bind(this.doc)
  createLink = this.doc.createLink.bind(this.doc)
  updateLink = this.doc.updateLink.bind(this.doc)
  deleteLink = this.doc.deleteLink.bind(this.doc)

  seekTo = this.doc.seekTo.bind(this.doc)
  _setCurrentTime = this.doc._setCurrentTime.bind(this.doc)
  importFromJson = this.doc.importFromJson.bind(this.doc)
  toJSON = this.doc.toJSON.bind(this.doc)
  on = this.doc.on.bind(this.doc)
  emit = this.doc.emit.bind(this.doc)

  protected _createView<T extends pub.AnyNode>(original: T): ViewType<VueTypeMap, T> {
    return this.vueScope.run(() => this.#createWithScope(original))!
  }

  #createWithScope<T extends pub.AnyNode>(original: T): ViewType<VueTypeMap, T> {
    let view

    switch (original.type) {
      case 'timeline':
      case 'track:video':
      case 'track:audio':
        view = new VueParentNode(this, original)
        break
      default:
        view = new VueNodeView(this, original)
    }

    return view as ViewType<VueTypeMap, T>
  }

  dispose(): void {
    this.doc.dispose()
    super.dispose()
  }
}
